<template>
  <div ref="privateEvent" class="AiPrivateEvent">
    <ai-image
      v-if="image"
      class="AiPrivateEvent-image"
      :src="image.src"
      :src-set="image.srcSet as string"
      loading="lazy" />
    <ai-typo class="AiPrivateEvent-title" as="h3" variant="heading-03">
      {{ title }}
    </ai-typo>

    <ai-typo
      v-if="shortText"
      as="p"
      variant="paragraph-04-regular"
      class="AiPrivateEvent-description">
      {{ displayText }}
    </ai-typo>

    <ai-typo
      v-else
      class="AiPrivateEvent-description"
      as="p"
      variant="paragraph-04-regular">
      {{ description }}
    </ai-typo>

    <ai-button
      v-if="shortText"
      :class="[
        'AiPrivateEvent-button--chevron',
        { 'AiPrivateEvent-button--chevron-expanded': expanded },
      ]"
      class="PresentationBlock-readMore"
      :label="buttonLabel"
      :variant="AiButtonVariant.Tertiary"
      :icon-right="'chevron' as IconName"
      slim
      :aria-expanded="expanded"
      :aria-controls="`description-` + slugify(title)"
      :aria-label="readMoreAriaLabel"
      @click="toggleExpanded" />
    <ai-link
      v-if="learnMoreUrl"
      typo-variant="button-bold"
      color="gold-900"
      :icon-right="'button-arrow-right' as IconName"
      :text="$t('hotel.card.buttonLabel')"
      :aria-label="$t('hotel.card.buttonLabel', { label: title })"
      :button="true"
      :url="learnMoreUrl" />
  </div>
</template>

<script setup lang="ts">
import type { IconName } from '../../atoms/AiIcon/types';
import { AiButtonVariant } from '../../atoms/AiButton/constants';

import { slugify } from '~~/helpers/index';
import type { AiPicture } from '~~/domains/graphql';
const { t } = useI18n();

const privateEvent = ref<HTMLElement>();

export interface AiPrivateEventProps {
  description: string;
  title: string;
  image?: AiPicture;
  learnMoreUrl?: string;
}
const props = defineProps<AiPrivateEventProps>();
const { expanded, shortText, displayText, toggleExpanded } = useExpandableText(
  props.description,
  250,
  300,
  privateEvent,
);
const buttonLabel = computed<string>(() =>
  expanded.value
    ? t('ux.organisms.carouselWithDescription.description.readLessCTA')
    : t('ux.organisms.carouselWithDescription.description.readMoreCTA'),
);
const readMoreAriaLabel = computed(() => props.title + ' ' + buttonLabel.value);
</script>
<style lang="scss">
@use '@/assets/styles/utilities/mixins';
@use '@/assets/styles/utilities/constants';
.AiPrivateEvent {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  align-items: flex-start;
}
.AiPrivateEvent-image {
  margin-bottom: constants.$margin-02;
  object-fit: cover;
  @include mixins.rem-fallback(height, 274);
}

.AiPrivateEvent-title {
  margin-bottom: constants.$margin-01;
}

.AiPrivateEvent-description {
  margin-bottom: constants.$margin-03;
  flex-grow: 1;
}
</style>
